import React, { useState, useEffect } from 'react';
import axios from '../api/axios';

interface EditSurveyProps {
  surveyId: string;
  onBack: () => void;
}

interface Question {
  id: string;
  type: string;
  title: string;
  description?: string;
  required: boolean;
  order: number;
  options?: string[];
  placeholder?: string;
  min_items?: number;
  max_items?: number;
  section_id?: string;
}

interface Section {
  id: string;
  title: string;
  description?: string;
  order: number;
}

interface Survey {
  id: string;
  title: string;
  description?: string;
  is_published: boolean;
  unique_url_slug?: string;
  questions: Question[];
  sections?: Section[];
}

const EditSurvey: React.FC<EditSurveyProps> = ({ surveyId, onBack }) => {
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [showEditSurveyInfo, setShowEditSurveyInfo] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [selectedSectionForNewQuestion, setSelectedSectionForNewQuestion] = useState<string>('');
  const [newQuestion, setNewQuestion] = useState({
    type: 'text',
    title: '',
    description: '',
    required: true,
    options: ['']
  });
  const [newSection, setNewSection] = useState({
    title: '',
    description: ''
  });
  const [editedSurveyInfo, setEditedSurveyInfo] = useState({
    title: '',
    description: '',
    unique_url_slug: ''
  });

  useEffect(() => {
    fetchSurvey();
  }, [surveyId]);

  useEffect(() => {
    if (survey) {
      setEditedSurveyInfo({
        title: survey.title,
        description: survey.description || '',
        unique_url_slug: survey.unique_url_slug || ''
      });
      // Initialize sections if not present
      if (!survey.sections) {
        survey.sections = [];
      }
    }
  }, [survey]);

  const fetchSurvey = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/surveys/${surveyId}`);
      setSurvey(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching survey:', err);
      setError(err.response?.data?.message || 'Failed to load survey');
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.title.trim()) {
      alert('Question title is required');
      return;
    }

    try {
      const payload: any = {
        type: newQuestion.type,
        title: newQuestion.title,
        description: newQuestion.description || undefined,
        required: newQuestion.required,
        order: survey?.questions.length || 0,
        section_id: selectedSectionForNewQuestion || undefined
      };

      // Add options for multiple choice questions
      if (['multiple_choice', 'checkboxes', 'dropdown'].includes(newQuestion.type)) {
        payload.options = newQuestion.options.filter(opt => opt.trim());
        if (payload.options.length === 0) {
          alert('At least one option is required for this question type');
          return;
        }
      }

      await axios.post(`/surveys/${surveyId}/questions`, payload);
      
      // Reset form and refresh survey
      setNewQuestion({
        type: 'text',
        title: '',
        description: '',
        required: true,
        options: ['']
      });
      setSelectedSectionForNewQuestion('');
      setShowAddQuestion(false);
      fetchSurvey();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      alert('Failed to add question: ' + (err.response?.data?.message || err.message));
      setSaveStatus('error');
    }
  };

  const handleEditQuestion = async () => {
    if (!editingQuestion || !editingQuestion.title.trim()) {
      alert('Question title is required');
      return;
    }

    try {
      setSaveStatus('saving');
      const payload: any = {
        type: editingQuestion.type,
        title: editingQuestion.title,
        description: editingQuestion.description || undefined,
        required: editingQuestion.required,
        order: editingQuestion.order
      };

      // Add options for multiple choice questions
      if (['multiple_choice', 'checkboxes', 'dropdown'].includes(editingQuestion.type)) {
        payload.options = editingQuestion.options?.filter(opt => opt.trim()) || [];
        if (payload.options.length === 0) {
          alert('At least one option is required for this question type');
          setSaveStatus('error');
          return;
        }
      }

      await axios.put(`/surveys/${surveyId}/questions/${editingQuestion.id}`, payload);
      
      setEditingQuestion(null);
      fetchSurvey();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      alert('Failed to update question: ' + (err.response?.data?.message || err.message));
      setSaveStatus('error');
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('Are you sure you want to delete this question?')) return;

    try {
      setSaveStatus('saving');
      await axios.delete(`/surveys/${surveyId}/questions/${questionId}`);
      fetchSurvey();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      alert('Failed to delete question: ' + (err.response?.data?.message || err.message));
      setSaveStatus('error');
    }
  };

  const handleMoveQuestion = async (questionId: string, direction: 'up' | 'down') => {
    if (!survey) return;
    
    const currentIndex = survey.questions.findIndex(q => q.id === questionId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= survey.questions.length) return;

    try {
      setSaveStatus('saving');
      const reorderedQuestions = [...survey.questions];
      [reorderedQuestions[currentIndex], reorderedQuestions[newIndex]] = 
        [reorderedQuestions[newIndex], reorderedQuestions[currentIndex]];
      
      // Update order for both questions
      await Promise.all([
        axios.put(`/surveys/${surveyId}/questions/${reorderedQuestions[currentIndex].id}`, {
          ...reorderedQuestions[currentIndex],
          order: currentIndex
        }),
        axios.put(`/surveys/${surveyId}/questions/${reorderedQuestions[newIndex].id}`, {
          ...reorderedQuestions[newIndex],
          order: newIndex
        })
      ]);
      
      fetchSurvey();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      alert('Failed to reorder questions: ' + (err.response?.data?.message || err.message));
      setSaveStatus('error');
    }
  };

  const handleDuplicateQuestion = async (question: Question) => {
    try {
      setSaveStatus('saving');
      const payload: any = {
        type: question.type,
        title: question.title + ' (Copy)',
        description: question.description,
        required: question.required,
        order: (survey?.questions.length || 0)
      };

      if (question.options) {
        payload.options = question.options;
      }

      await axios.post(`/surveys/${surveyId}/questions`, payload);
      fetchSurvey();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      alert('Failed to duplicate question: ' + (err.response?.data?.message || err.message));
      setSaveStatus('error');
    }
  };

  const handleMoveSection = async (sectionId: string, direction: 'up' | 'down') => {
    console.log('🔥 [MOVE_SECTION] BUTTON CLICKED!', { sectionId, direction });
    
    if (!survey || !survey.sections) {
      console.error('📂 [MOVE_SECTION] No survey or sections');
      return;
    }
    
    // Work with sorted sections to match displayed order
    const sortedSections = [...survey.sections].sort((a, b) => a.order - b.order);
    console.log('📂 [MOVE_SECTION] All sections before move:', sortedSections.map(s => ({ id: s.id, title: s.title, order: s.order })));
    
    const currentIndex = sortedSections.findIndex(s => s.id === sectionId);
    if (currentIndex === -1) {
      console.error('📂 [MOVE_SECTION] Section not found:', sectionId);
      return;
    }
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= sortedSections.length) {
      console.warn('📂 [MOVE_SECTION] Cannot move beyond boundaries');
      return;
    }

    try {
      setSaveStatus('saving');
      
      // Get the two sections to swap
      const section1 = sortedSections[currentIndex];
      const section2 = sortedSections[newIndex];
      
      console.log('📂 [MOVE_SECTION] Swapping:', {
        section1: { id: section1.id, title: section1.title, oldOrder: section1.order },
        section2: { id: section2.id, title: section2.title, oldOrder: section2.order }
      });
      
      // Swap their order values
      const temp = section1.order;
      section1.order = section2.order;
      section2.order = temp;
      
      console.log('📂 [MOVE_SECTION] After swap:', {
        section1: { id: section1.id, title: section1.title, newOrder: section1.order },
        section2: { id: section2.id, title: section2.title, newOrder: section2.order }
      });
      
      // Update both sections in the database
      await Promise.all([
        axios.put(`/surveys/${surveyId}/sections/${section1.id}`, {
          title: section1.title,
          description: section1.description,
          order: section1.order
        }),
        axios.put(`/surveys/${surveyId}/sections/${section2.id}`, {
          title: section2.title,
          description: section2.description,
          order: section2.order
        })
      ]);
      
      console.log('📂 [MOVE_SECTION] API calls successful, refreshing survey...');
      
      // Refresh from server to get updated order
      await fetchSurvey();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      console.error('📂 [MOVE_SECTION] Error:', err);
      alert('Failed to move section: ' + (err.response?.data?.error || err.message));
      setSaveStatus('error');
    }
  };

  const handleUpdateSurveyInfo = async () => {
    try {
      setSaveStatus('saving');
      await axios.put(`/surveys/${surveyId}`, {
        title: editedSurveyInfo.title,
        description: editedSurveyInfo.description,
        unique_url_slug: editedSurveyInfo.unique_url_slug
      });
      
      setShowEditSurveyInfo(false);
      fetchSurvey();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      alert('Failed to update survey: ' + (err.response?.data?.message || err.message));
      setSaveStatus('error');
    }
  };

  const handleAddSection = async () => {
    if (!newSection.title.trim()) {
      alert('Section title is required');
      return;
    }

    try {
      setSaveStatus('saving');
      const payload = {
        title: newSection.title,
        description: newSection.description || undefined,
        order: survey?.sections?.length || 0
      };

      const response = await axios.post(`/surveys/${surveyId}/sections`, payload);
      
      setNewSection({ title: '', description: '' });
      setShowAddSection(false);
      fetchSurvey();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      // If sections endpoint doesn't exist, store locally
      const newSectionObj: Section = {
        id: `section-${Date.now()}`,
        title: newSection.title,
        description: newSection.description,
        order: survey?.sections?.length || 0
      };
      setSurvey(prev => prev ? {
        ...prev,
        sections: [...(prev.sections || []), newSectionObj]
      } : null);
      setNewSection({ title: '', description: '' });
      setShowAddSection(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const handleEditSection = async () => {
    if (!editingSection || !editingSection.title.trim()) {
      alert('Section title is required');
      return;
    }

    try {
      setSaveStatus('saving');
      await axios.put(`/surveys/${surveyId}/sections/${editingSection.id}`, {
        title: editingSection.title,
        description: editingSection.description,
        order: editingSection.order
      });
      
      setEditingSection(null);
      fetchSurvey();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      // Update locally if API doesn't exist
      setSurvey(prev => prev ? {
        ...prev,
        sections: (prev.sections || []).map(s => 
          s.id === editingSection.id ? editingSection : s
        )
      } : null);
      setEditingSection(null);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!confirm('Delete this section? Questions in this section will be moved to "No Section".')) return;

    try {
      setSaveStatus('saving');
      await axios.delete(`/surveys/${surveyId}/sections/${sectionId}`);
      fetchSurvey();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: any) {
      // Delete locally if API doesn't exist
      setSurvey(prev => prev ? {
        ...prev,
        sections: (prev.sections || []).filter(s => s.id !== sectionId),
        questions: prev.questions.map(q => 
          q.section_id === sectionId ? { ...q, section_id: undefined } : q
        )
      } : null);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const handlePublishToggle = async () => {
    if (!survey) return;

    try {
      await axios.put(`/surveys/${surveyId}`, {
        is_published: !survey.is_published
      });
      setSurvey({ ...survey, is_published: !survey.is_published });
    } catch (err: any) {
      alert('Failed to update survey: ' + (err.response?.data?.message || err.message));
    }
  };

  const renderQuestion = (question: Question, index: number) => {
    const questionTypes = [
      { value: 'text', label: 'Short Text' },
      { value: 'textarea', label: 'Long Text' },
      { value: 'multiple_choice', label: 'Multiple Choice' },
      { value: 'checkboxes', label: 'Checkboxes' },
      { value: 'dropdown', label: 'Dropdown' },
      { value: 'number', label: 'Number' },
      { value: 'email', label: 'Email' },
      { value: 'date', label: 'Date' },
      { value: 'rating', label: 'Rating' }
    ];

    return (
      <div
        style={{
          background: editingQuestion?.id === question.id ? 'var(--accent-bg)' : 'var(--bg-secondary)',
          border: editingQuestion?.id === question.id ? '2px solid var(--accent)' : '1px solid var(--input-border)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem',
          transition: 'all 0.2s'
        }}
      >
        {editingQuestion?.id === question.id ? (
          // Edit Mode
          <div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                Question Type
              </label>
              <select
                value={editingQuestion.type}
                onChange={(e) => setEditingQuestion({ ...editingQuestion, type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)'
                }}
              >
                {questionTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                Question Text *
              </label>
              <input
                type="text"
                value={editingQuestion.title}
                onChange={(e) => setEditingQuestion({ ...editingQuestion, title: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                Description (optional)
              </label>
              <input
                type="text"
                value={editingQuestion.description || ''}
                onChange={(e) => setEditingQuestion({ ...editingQuestion, description: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            {['multiple_choice', 'checkboxes', 'dropdown'].includes(editingQuestion.type) && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                  Options
                </label>
                {(editingQuestion.options || ['']).map((option, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...(editingQuestion.options || [''])];
                        newOptions[idx] = e.target.value;
                        setEditingQuestion({ ...editingQuestion, options: newOptions });
                      }}
                      placeholder={`Option ${idx + 1}`}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        border: '1px solid var(--input-border)',
                        borderRadius: '6px',
                        background: 'var(--input-bg)',
                        color: 'var(--text-primary)'
                      }}
                    />
                    {(editingQuestion.options || ['']).length > 1 && (
                      <button
                        onClick={() => {
                          const newOptions = (editingQuestion.options || ['']).filter((_, i) => i !== idx);
                          setEditingQuestion({ ...editingQuestion, options: newOptions });
                        }}
                        style={{
                          background: 'var(--error-bg)',
                          color: 'var(--error)',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setEditingQuestion({ ...editingQuestion, options: [...(editingQuestion.options || ['']), ''] })}
                  style={{
                    background: 'var(--accent-bg)',
                    color: 'var(--accent)',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  + Add Option
                </button>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editingQuestion.required}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, required: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>Required Question</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingQuestion(null)}
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleEditQuestion}
                style={{
                  background: 'var(--button-bg)',
                  color: 'var(--button-text)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        ) : (
          // View Mode
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'start' }}>
            <div style={{
              background: 'var(--button-bg)',
              color: 'var(--button-text)',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              flexShrink: 0
            }}>
              {index + 1}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                {question.title}
                {question.required && <span style={{ color: 'var(--error)' }}> *</span>}
              </div>
              {question.description && (
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  {question.description}
                </div>
              )}
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                Type: {questionTypes.find(t => t.value === question.type)?.label || question.type}
                {question.options && question.options.length > 0 && (
                  <span> • {question.options.length} options</span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button
                  onClick={() => handleMoveQuestion(question.id, 'up')}
                  disabled={index === 0}
                  style={{
                    background: index === 0 ? 'var(--bg-secondary)' : 'var(--button-bg)',
                    color: index === 0 ? 'var(--text-secondary)' : 'var(--button-text)',
                    border: 'none',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem'
                  }}
                  title="Move up"
                >
                  ▲
                </button>
                <button
                  onClick={() => handleMoveQuestion(question.id, 'down')}
                  disabled={index === (survey?.questions.length || 0) - 1}
                  style={{
                    background: index === (survey?.questions.length || 0) - 1 ? 'var(--bg-secondary)' : 'var(--button-bg)',
                    color: index === (survey?.questions.length || 0) - 1 ? 'var(--text-secondary)' : 'var(--button-text)',
                    border: 'none',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    cursor: index === (survey?.questions.length || 0) - 1 ? 'not-allowed' : 'pointer',
                    fontSize: '0.85rem'
                  }}
                  title="Move down"
                >
                  ▼
                </button>
              </div>
              <button
                onClick={() => setEditingQuestion(question)}
                style={{
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
                title="Edit question"
              >
                ✏️ Edit
              </button>
              <button
                onClick={() => handleDuplicateQuestion(question)}
                style={{
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
                title="Duplicate question"
              >
                📋 Copy
              </button>
              <button
                onClick={() => handleDeleteQuestion(question.id)}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
                title="Delete question"
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAddQuestionForm = () => {
    const questionTypes = [
      { value: 'text', label: 'Short Text' },
      { value: 'textarea', label: 'Long Text' },
      { value: 'multiple_choice', label: 'Multiple Choice' },
      { value: 'checkboxes', label: 'Checkboxes' },
      { value: 'dropdown', label: 'Dropdown' },
      { value: 'number', label: 'Number' },
      { value: 'email', label: 'Email' },
      { value: 'date', label: 'Date' },
      { value: 'rating', label: 'Rating' }
    ];

    return (
      <div style={{
        background: 'var(--bg-secondary)',
        border: '2px solid var(--card-border)',
        borderRadius: '8px',
        padding: '1.5rem',
        marginTop: '1rem'
      }}>
        <h4 style={{ margin: '0 0 1rem', fontSize: '1.2rem', color: 'var(--text-primary)' }}>Add New Question</h4>
        
        {survey.sections && survey.sections.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              Add to Section (optional)
            </label>
            <select
              value={selectedSectionForNewQuestion}
              onChange={(e) => setSelectedSectionForNewQuestion(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--input-border)',
                borderRadius: '6px',
                fontSize: '1rem',
                background: 'var(--input-bg)',
                color: 'var(--text-primary)'
              }}
            >
              <option value="">No Section</option>
              {survey.sections.sort((a, b) => a.order - b.order).map(section => (
                <option key={section.id} value={section.id}>{section.title}</option>
              ))}
            </select>
          </div>
        )}
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
            Question Type
          </label>
          <select
            value={newQuestion.type}
            onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid var(--input-border)',
              borderRadius: '6px',
              fontSize: '1rem',
              background: 'var(--input-bg)',
              color: 'var(--text-primary)'
            }}
          >
            {questionTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
            Question Text *
          </label>
          <input
            type="text"
            value={newQuestion.title}
            onChange={(e) => setNewQuestion({ ...newQuestion, title: e.target.value })}
            placeholder="Enter your question"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid var(--input-border)',
              borderRadius: '6px',
              fontSize: '1rem',
              boxSizing: 'border-box',
              background: 'var(--input-bg)',
              color: 'var(--text-primary)'
            }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
            Description (optional)
          </label>
          <input
            type="text"
            value={newQuestion.description}
            onChange={(e) => setNewQuestion({ ...newQuestion, description: e.target.value })}
            placeholder="Additional details"
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid var(--input-border)',
              borderRadius: '6px',
              fontSize: '1rem',
              boxSizing: 'border-box',
              background: 'var(--input-bg)',
              color: 'var(--text-primary)'
            }}
          />
        </div>

        {['multiple_choice', 'checkboxes', 'dropdown'].includes(newQuestion.type) && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              Options
            </label>
            {newQuestion.options.map((option, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  value={option}
                  onChange={(e) => {
                    const newOptions = [...newQuestion.options];
                    newOptions[idx] = e.target.value;
                    setNewQuestion({ ...newQuestion, options: newOptions });
                  }}
                  placeholder={`Option ${idx + 1}`}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    border: '1px solid var(--input-border)',
                    borderRadius: '6px',
                    background: 'var(--input-bg)',
                    color: 'var(--text-primary)'
                  }}
                />
                {newQuestion.options.length > 1 && (
                  <button
                    onClick={() => {
                      const newOptions = newQuestion.options.filter((_, i) => i !== idx);
                      setNewQuestion({ ...newQuestion, options: newOptions });
                    }}
                    style={{
                      background: 'var(--error-bg)',
                      color: 'var(--error)',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={() => setNewQuestion({ ...newQuestion, options: [...newQuestion.options, ''] })}
              style={{
                background: 'var(--accent-bg)',
                color: 'var(--accent)',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              + Add Option
            </button>
          </div>
        )}

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={newQuestion.required}
              onChange={(e) => setNewQuestion({ ...newQuestion, required: e.target.checked })}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>Required Question</span>
          </label>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              setShowAddQuestion(false);
              setSelectedSectionForNewQuestion('');
              setNewQuestion({
                type: 'text',
                title: '',
                description: '',
                required: true,
                options: ['']
              });
            }}
            style={{
              background: 'var(--bg-secondary)',
              color: 'var(--text-secondary)',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleAddQuestion}
            style={{
              background: 'var(--button-bg)',
              color: 'var(--button-text)',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Add Question
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>Loading survey...</div>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div>
        <button onClick={onBack} style={{ marginBottom: '1rem', cursor: 'pointer' }}>
          ← Back
        </button>
        <div style={{ background: 'var(--error-bg)', padding: '1rem', borderRadius: '8px', color: 'var(--error)' }}>
          {error || 'Survey not found'}
        </div>
      </div>
    );
  }

  const questionTypes = [
    { value: 'text', label: 'Short Text' },
    { value: 'textarea', label: 'Long Text' },
    { value: 'multiple_choice', label: 'Multiple Choice' },
    { value: 'checkboxes', label: 'Checkboxes' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'date', label: 'Date' },
    { value: 'rating', label: 'Rating' }
  ];

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--accent)',
            cursor: 'pointer',
            fontSize: '1rem',
            padding: '0.5rem 0'
          }}
        >
          ← Back to Surveys
        </button>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {saveStatus === 'saving' && (
            <span style={{ color: 'var(--warning)', fontSize: '0.9rem' }}>⏳ Saving...</span>
          )}
          {saveStatus === 'saved' && (
            <span style={{ color: 'var(--success)', fontSize: '0.9rem' }}>✓ Saved</span>
          )}
          {saveStatus === 'error' && (
            <span style={{ color: 'var(--error)', fontSize: '0.9rem' }}>✗ Error</span>
          )}
          <button
            onClick={handlePublishToggle}
            style={{
              background: survey.is_published ? 'var(--accent-bg)' : 'var(--success-bg)',
              color: survey.is_published ? 'var(--accent)' : 'var(--success)',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {survey.is_published ? '○ Unpublish' : '✓ Publish'}
          </button>
        </div>
      </div>

      <div style={{
        background: 'var(--card-bg)',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.8rem' }}>{survey.title}</h2>
            {survey.description && (
              <p style={{ margin: '0 0 0.5rem', color: 'var(--text-secondary)' }}>{survey.description}</p>
            )}
            {survey.unique_url_slug && (
              <p style={{ margin: '0.5rem 0 0', color: 'var(--accent)', fontSize: '0.9rem', fontFamily: 'monospace' }}>
                URL: /survey/{survey.unique_url_slug}
              </p>
            )}
          </div>
          <button
            onClick={() => setShowEditSurveyInfo(true)}
            style={{
              background: 'var(--button-bg)',
              color: 'var(--button-text)',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
              marginLeft: '1rem'
            }}
          >
            ✏️ Edit Info
          </button>
        </div>
        <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          <span style={{ 
            color: survey.is_published ? 'var(--success)' : 'var(--warning)',
            fontWeight: 'bold'
          }}>
            {survey.is_published ? '✓ Published' : '○ Draft'}
          </span>
          <span style={{ margin: '0 0.5rem' }}>•</span>
          <span>{survey.questions.length} questions</span>
        </div>
      </div>

      <div style={{
        background: 'var(--card-bg)',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.5rem' }}>Questions</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {!showAddSection && (
              <button
                onClick={() => setShowAddSection(true)}
                style={{
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                + Add Section
              </button>
            )}
            {!showAddQuestion && (
              <button
                onClick={() => setShowAddQuestion(true)}
                style={{
                  background: 'var(--button-bg)',
                  color: 'var(--button-text)',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                + Add Question
              </button>
            )}
          </div>
        </div>

        {survey.questions.length === 0 && !showAddQuestion && !showAddSection && (
          <div style={{
            background: 'var(--bg-secondary)',
            border: '2px dashed var(--input-border)',
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            color: 'var(--text-secondary)'
          }}>
            No questions yet. Add your first question to get started.
          </div>
        )}

        {/* Add Section Form */}
        {showAddSection && (
          <div style={{
            background: '#fff5e6',
            border: '2px solid #f59e0b',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1rem'
          }}>
            <h4 style={{ margin: '0 0 1rem', fontSize: '1.2rem', color: 'var(--text-primary)' }}>Add New Section</h4>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                Section Title *
              </label>
              <input
                type="text"
                value={newSection.title}
                onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
                placeholder="e.g., Personal Information"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                  background: 'white',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                Description (optional)
              </label>
              <input
                type="text"
                value={newSection.description}
                onChange={(e) => setNewSection({ ...newSection, description: e.target.value })}
                placeholder="Brief description of this section"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                  background: 'white',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddSection(false);
                  setNewSection({ title: '', description: '' });
                }}
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddSection}
                style={{
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Add Section
              </button>
            </div>
          </div>
        )}

        {/* Render sections and questions */}
        {(() => {
          const sections = survey.sections || [];
          const questionsWithoutSection = survey.questions.filter(q => !q.section_id);
          const sortedSections = [...sections].sort((a, b) => a.order - b.order);

          return (
            <>
              {/* Questions without a section */}
              {questionsWithoutSection.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  {questionsWithoutSection.map((question, index) => (
                    <div key={question.id}>
                      {renderQuestion(question, index)}
                    </div>
                  ))}
                </div>
              )}

              {/* Sections with their questions */}
              {sortedSections.map((section, sectionIndex) => {
                const sectionQuestions = survey.questions.filter(q => q.section_id === section.id);
                const isFirstSection = sectionIndex === 0;
                const isLastSection = sectionIndex === sortedSections.length - 1;
                const canMoveUp = sectionIndex > 0 && sortedSections.length > 1;
                const canMoveDown = sectionIndex < sortedSections.length - 1 && sortedSections.length > 1;
                
                console.log(`🔍 Section "${section.title}" - index: ${sectionIndex}, total: ${sortedSections.length}, canMoveUp: ${canMoveUp}, canMoveDown: ${canMoveDown}`);
                
                return (
                  <div key={section.id} style={{ marginBottom: '2rem' }}>
                    {editingSection?.id === section.id ? (
                      // Edit section mode
                      <div style={{
                        background: '#fff5e6',
                        border: '2px solid #f59e0b',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        marginBottom: '1rem'
                      }}>
                        <div style={{ marginBottom: '1rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            Section Title *
                          </label>
                          <input
                            type="text"
                            value={editingSection.title}
                            onChange={(e) => setEditingSection({ ...editingSection, title: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid var(--input-border)',
                              borderRadius: '6px',
                              fontSize: '1rem',
                              boxSizing: 'border-box',
                              background: 'white'
                            }}
                          />
                        </div>
                        <div style={{ marginBottom: '1rem' }}>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            Description
                          </label>
                          <input
                            type="text"
                            value={editingSection.description || ''}
                            onChange={(e) => setEditingSection({ ...editingSection, description: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid var(--input-border)',
                              borderRadius: '6px',
                              fontSize: '1rem',
                              boxSizing: 'border-box',
                              background: 'white'
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => setEditingSection(null)}
                            style={{
                              background: 'var(--bg-secondary)',
                              color: 'var(--text-secondary)',
                              border: 'none',
                              padding: '0.75rem 1.5rem',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleEditSection}
                            style={{
                              background: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              padding: '0.75rem 1.5rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                          >
                            Save Changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View section mode
                      <div style={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        padding: '1rem 1.5rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <h4 style={{ margin: '0 0 0.25rem', color: 'white', fontSize: '1.2rem' }}>
                            📂 {section.title}
                          </h4>
                          {section.description && (
                            <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                              {section.description}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', position: 'relative', zIndex: 100 }}>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log(`⬆️ UP button clicked for section: ${section.title}, canMove: ${canMoveUp}`);
                              handleMoveSection(section.id, 'up');
                            }}
                            disabled={!canMoveUp}
                            style={{
                              background: !canMoveUp ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.3)',
                              padding: '0.5rem 1rem',
                              borderRadius: '6px',
                              cursor: !canMoveUp ? 'not-allowed' : 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 'bold',
                              opacity: !canMoveUp ? 0.5 : 1,
                              pointerEvents: !canMoveUp ? 'none' : 'auto',
                              position: 'relative',
                              zIndex: 101
                            }}
                          >
                            ⬆️
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log(`⬇️ DOWN button clicked for section: ${section.title}, canMove: ${canMoveDown}`);
                              handleMoveSection(section.id, 'down');
                            }}
                            disabled={!canMoveDown}
                            style={{
                              background: !canMoveDown ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.3)',
                              padding: '0.5rem 1rem',
                              borderRadius: '6px',
                              cursor: !canMoveDown ? 'not-allowed' : 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 'bold',
                              opacity: !canMoveDown ? 0.5 : 1,
                              pointerEvents: !canMoveDown ? 'none' : 'auto',
                              position: 'relative',
                              zIndex: 101
                            }}
                          >
                            ⬇️
                          </button>
                          <button
                            onClick={() => setEditingSection(section)}
                            style={{
                              background: 'rgba(255,255,255,0.2)',
                              color: 'white',
                              border: '1px solid rgba(255,255,255,0.3)',
                              padding: '0.5rem 1rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 'bold',
                              pointerEvents: 'auto',
                              position: 'relative',
                              zIndex: 10
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteSection(section.id)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.9)',
                              color: 'white',
                              border: 'none',
                              padding: '0.5rem 1rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 'bold'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {sectionQuestions.map((question, index) => (
                      <div key={question.id}>
                        {renderQuestion(question, index)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </>
          );
        })()}

        {showAddQuestion && renderAddQuestionForm()}
      </div>

      {/* Edit Survey Info Modal */}
      {showEditSurveyInfo && (
        <div
          onClick={() => setShowEditSurveyInfo(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card-bg)',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            <h3 style={{ margin: '0 0 1.5rem', fontSize: '1.5rem', color: 'var(--text-primary)' }}>
              Edit Survey Information
            </h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                Survey Title *
              </label>
              <input
                type="text"
                value={editedSurveyInfo.title}
                onChange={(e) => setEditedSurveyInfo({ ...editedSurveyInfo, title: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                Description
              </label>
              <textarea
                value={editedSurveyInfo.description}
                onChange={(e) => setEditedSurveyInfo({ ...editedSurveyInfo, description: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                URL Slug
              </label>
              <input
                type="text"
                value={editedSurveyInfo.unique_url_slug}
                onChange={(e) => setEditedSurveyInfo({ ...editedSurveyInfo, unique_url_slug: e.target.value })}
                placeholder="unique-url-slug"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--input-border)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)',
                  fontFamily: 'monospace'
                }}
              />
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.5rem 0 0' }}>
                Survey will be available at: /survey/{editedSurveyInfo.unique_url_slug || 'your-slug'}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEditSurveyInfo(false)}
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-secondary)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateSurveyInfo}
                style={{
                  background: 'var(--button-bg)',
                  color: 'var(--button-text)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditSurvey;
